import React from 'react';
import PropTypes from 'prop-types';

/**
 * Collapsible accordion group for the SuperAdmin sidebar.
 *
 * Renders a group header (button) plus a panel that wraps its children.
 * The parent owns the open/close state so multiple groups can be expanded
 * at once and the collection can be persisted in localStorage. Leaf items
 * inside `children` are kept mounted in the DOM even when collapsed so
 * assertions like `getAllByRole('tab')` (used in `SuperAdmin.sidebar.test.js`)
 * still see them — visibility is controlled by the panel's `hidden` attribute.
 *
 * @param {Object} props
 * @param {string} props.id - Stable identifier used for the group's DOM ids,
 *   `${id}-header` on the button and `${id}-panel` on the panel, and for
 *   data-testid hooks. Must be unique among sidebar groups.
 * @param {string} props.title - Human-readable group label rendered inside
 *   the header (e.g. "Manage", "Operations").
 * @param {boolean} props.isOpen - Whether the group panel is currently
 *   expanded. Reflected in the button's `aria-expanded` and drives whether
 *   the panel gets the `hidden` attribute.
 * @param {() => void} props.onToggle - Callback fired when the operator
 *   clicks the header or presses Enter/Space on it. The parent decides
 *   how to update state (single-open vs. multi-open semantics).
 * @param {React.ReactNode} props.children - The group's leaf items,
 *   typically the sidebar's `<button role="tab">` elements.
 * @returns {JSX.Element} The accordion group element.
 * @example
 * const [open, setOpen] = useState(true);
 * <AdminSidebarGroup
 *   id="ops"
 *   title="Operations"
 *   isOpen={open}
 *   onToggle={() => setOpen((v) => !v)}
 * >
 *   <button role="tab">CRM</button>
 * </AdminSidebarGroup>
 */
function AdminSidebarGroup({ id, title, isOpen, onToggle, children = null }) {
  const headerId = `${id}-header`;
  const panelId = `${id}-panel`;

  /**
   * Toggle on Enter or Space. Native <button> already activates on click for
   * these keys, but jsdom does not synthesize the click from keyDown events,
   * so we handle them explicitly so keyboard-only tests exercise onToggle.
   *
   * @param {React.KeyboardEvent<HTMLButtonElement>} event
   * @returns {void}
   */
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      onToggle();
    }
  };

  const caretClass = `admin-sidebar__group-caret${
    isOpen ? ' admin-sidebar__group-caret--open' : ''
  }`;

  return (
    <div
      className={`admin-sidebar__group${isOpen ? '' : ' admin-sidebar__group--collapsed'}`}
      data-testid={`admin-sidebar-group-${id}`}
    >
      <button
        type="button"
        role="button"
        id={headerId}
        className="admin-sidebar__group-header"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        data-testid={`admin-sidebar-group-header-${id}`}
        data-state={isOpen ? 'open' : 'closed'}
      >
        <span className="admin-sidebar__group-header-label">{title}</span>
        <span
          className={caretClass}
          data-state={isOpen ? 'open' : 'closed'}
          aria-hidden="true"
        >
          <svg viewBox="0 0 6 10" focusable="false" aria-hidden="true">
            <path d="M1 1l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>
      <div
        role="group"
        id={panelId}
        aria-labelledby={headerId}
        className="admin-sidebar__group-panel"
        hidden={!isOpen}
      >
        {children}
      </div>
    </div>
  );
}

AdminSidebarGroup.propTypes = {
  /** Stable id for DOM ids and data-testid hooks (must be unique per sidebar). */
  id: PropTypes.string.isRequired,
  /** Visible group label. */
  title: PropTypes.string.isRequired,
  /** Whether the panel is expanded; controlled by the parent. */
  isOpen: PropTypes.bool.isRequired,
  /** Fired on header click or Enter/Space. */
  onToggle: PropTypes.func.isRequired,
  /** Leaf items rendered inside the panel. */
  children: PropTypes.node,
};

export default AdminSidebarGroup;
